import { KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { IMAGE_SCALE_MAX, IMAGE_SCALE_MIN } from "./model/imageScale";
import useImageScaleField, { type GetEditor, type ImageScaleAnchor } from "./model/useImageScaleField";

interface ImageScaleFieldProps {
    getQuill: GetEditor;
    enabled: boolean;
}

interface PanelBox {
    left: number;
    top: number;
    x: number;
    y: number;
}

const PANEL_WIDTH = 360;
const PANEL_HEIGHT = 40;
const VIEWPORT_MARGIN = 8;

const placeNearCursor = (anchor: ImageScaleAnchor, width: number, height: number) => {
    const margin = VIEWPORT_MARGIN;
    let left = anchor.x + margin;
    let top = anchor.y + margin;
    if (left + width > window.innerWidth - margin) left = anchor.x - width - margin;
    if (top + height > window.innerHeight - margin) top = anchor.y - height - margin;
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
    top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));
    return { left, top, x: anchor.x, y: anchor.y };
};

const focusScaleInput = (input: HTMLInputElement | null) => {
    if (!input || input.disabled || document.activeElement === input) return;
    input.focus({ preventScroll: true });
    input.select();
};

/** Offcanvas and modal keep focus inside themselves. A panel on document.body is pulled back at once. */
const panelHost = (getQuill: GetEditor): HTMLElement => {
    const root = getQuill()?.root;
    return root?.closest<HTMLElement>(".offcanvas, .modal") ?? document.body;
};

const ImageScaleField = ({ getQuill, enabled }: ImageScaleFieldProps) => {
    const { t } = useTranslation("common");
    const field = useImageScaleField(getQuill, enabled);
    const panelRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [box, setBox] = useState<PanelBox | null>(null);

    const placed = field.anchor
        && box
        && box.x === field.anchor.x
        && box.y === field.anchor.y
        ? box
        : field.anchor
            ? placeNearCursor(field.anchor, PANEL_WIDTH, PANEL_HEIGHT)
            : null;

    useLayoutEffect(() => {
        const node = panelRef.current;
        if (!field.anchor || !node) return;
        const rect = node.getBoundingClientRect();
        const next = placeNearCursor(field.anchor, rect.width, rect.height);
        setBox((prev) => (
            prev
            && prev.left === next.left
            && prev.top === next.top
            && prev.x === next.x
            && prev.y === next.y
                ? prev
                : next
        ));
        focusScaleInput(inputRef.current);
    }, [field.anchor, field.ready]);

    useEffect(() => {
        if (!field.open) return;
        const editorRoot = getQuill()?.root ?? null;

        const onFocusIn = (event: FocusEvent) => {
            const panel = panelRef.current;
            const next = event.target;
            if (!panel || !(next instanceof Node) || panel.contains(next)) return;
            if (!editorRoot || (next !== editorRoot && !editorRoot.contains(next))) return;
            queueMicrotask(() => focusScaleInput(inputRef.current));
        };

        document.addEventListener("focusin", onFocusIn, true);
        return () => document.removeEventListener("focusin", onFocusIn, true);
    }, [field.open, getQuill]);

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        if (field.canSave) field.save();
    };

    if (!field.open || !placed) return null;

    return createPortal(
        <div
            ref={panelRef}
            className="editor-image-scale"
            data-editor-image-scale=""
            style={{ left: placed.left, top: placed.top }}
            onMouseDown={(event) => event.stopPropagation()}
            onMouseUp={(event) => event.stopPropagation()}
        >
            <span className="editor-image-scale__label">{t("imageScale.label")}</span>
            <input
                ref={inputRef}
                id="editor-image-scale"
                className="editor-image-scale__input"
                type="number"
                inputMode="numeric"
                aria-label={t("imageScale.hint")}
                title={t("imageScale.hint")}
                min={IMAGE_SCALE_MIN}
                max={IMAGE_SCALE_MAX}
                step={1}
                value={field.draft}
                disabled={!field.ready}
                onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, "").slice(0, 3);
                    field.setDraft(digits);
                }}
                onKeyDown={onKeyDown}
            />
            <span className="editor-image-scale__unit">%</span>
            <button
                type="button"
                className="editor-image-scale__btn"
                title={t("buttons.save")}
                aria-label={t("buttons.save")}
                onClick={field.save}
                disabled={!field.canSave}
            >
                <svg width="16" height="16" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                    <path d="M43 11L16.875 37L5 25.1818" stroke="#008a00" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            <button
                type="button"
                className="editor-image-scale__btn"
                title={t("buttons.cancel")}
                aria-label={t("buttons.cancel")}
                onClick={field.close}
            >
                <svg width="16" height="16" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                    <path d="M8 8L40 40" stroke="#db3700" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8 40L40 8" stroke="#db3700" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
        </div>,
        panelHost(getQuill),
    );
};

export default ImageScaleField;
